import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as dayjs from 'dayjs';
import { Observable } from 'rxjs';
import { clientSettings } from 'go-modules/models/common/client.settings';
import { UserRoleLocalizationUtil } from '@go-modules/utilities/user-role-localization/user-role-localization.util';
import type { Roles } from '@go-modules/enums/roles';
import type { ApiInvite, Invite } from '@go-modules/interfaces/invite/invite';

@Injectable({
	providedIn: 'root'
})
export class NgxInviteService {
	constructor (private http: HttpClient) {}

	public invites (groupId: number): Observable<ApiInvite[]> {
		return this.http.get<ApiInvite[]>(`${clientSettings.GoReactV2API}/groups/${groupId}/invites`);
	}

	public sendInvites (groupId: number, invites: Invite[]): Observable<any> {
		const payload = invites.map((invite): ApiInvite => this.toApiInvite(invite));
		return this.http.post(`${clientSettings.GoReactV2API}/groups/${groupId}/invites`, payload);
	}

	public resend (groupId: number, inviteId: number): Observable<any> {
		return this.http.post<ApiInvite>(
			`${clientSettings.GoReactV2API}/groups/${groupId}/invites/${inviteId}/resend`,
			null
		);
	}

	public deleteInvite (groupId: number, inviteId: number): Observable<void> {
		return this.http.delete<void>(`${clientSettings.GoReactV2API}/groups/${groupId}/invites/${inviteId}`);
	}

	public normalizeInvites (invites: ApiInvite[]): Invite[] {
		return invites.map((invite) => this.normalizeInvite(invite));
	}

	private normalizeInvite (invite: ApiInvite): Invite {
		const lastSent = invite.last_sent ? dayjs.utc(invite.last_sent).toDate() : null;
		const roleLocalized = invite.role
			? UserRoleLocalizationUtil.localize(invite.role as Roles)
			: null;
		return {
			inviteId: invite.invite_id,
			uuid: invite.uuid,
			groupId: invite.group_id,
			userId: invite.user_id,
			active: invite.active,
			email: invite.email,
			role: invite.role,
			lastSent: lastSent || new Date(),
			message: invite.message,
			role_localized: roleLocalized
		} as Invite;
	}

	private toApiInvite (invite: Invite): ApiInvite {
		return {
			invite_id: invite.inviteId,
			group_id: invite.groupId,
			user_id: invite.userId,
			uuid: invite.uuid,
			active: invite.active,
			email: this.cleanEmail(invite.email),
			role: invite.role,
			last_sent: invite.lastSent
				? dayjs(invite.lastSent).utc().format('YYYY-MM-DD HH:mm:ss')
				: undefined,
			message: invite.message
		};
	}

	private cleanEmail (email: string): string {
		return email.replace(/<\s*\w.*?>/, (reformEmail) => {
			reformEmail = reformEmail.replace('<', '');
			reformEmail = reformEmail.replace('>', '');
			return reformEmail;
		}).replace(/^\s\s*/, '').replace(/\s\s*$/, '');
	}
}
